import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ReactPhotoSphereViewer } from "react-photo-sphere-viewer";
import { GyroscopePlugin } from "@photo-sphere-viewer/gyroscope-plugin";
import { StereoPlugin } from "@photo-sphere-viewer/stereo-plugin";
import { MarkersPlugin } from "@photo-sphere-viewer/markers-plugin";
import "@photo-sphere-viewer/markers-plugin/index.css";
import { QRCodeCanvas } from "qrcode.react";
import { Plus, Trash2, Sparkles, Upload, Eye, X, Loader2, Globe, Wand2, Clipboard, Smartphone, Share2, Check, Play, Pause, MapPin, Link2, QrCode, Route, GripVertical, Camera, Zap, Download } from "lucide-react";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Label } from "./components/ui/label";
import { Textarea } from "./components/ui/textarea";
import { Card, CardContent } from "./components/ui/card";
import { Badge } from "./components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "./components/ui/dialog";
import { Popover, PopoverTrigger, PopoverContent } from "./components/ui/popover";
import AiProgressBar from "./components/AiProgressBar";
import VirtualTourViewer from "./VirtualTourViewer";
import WalkableViewer from "./WalkableViewer";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Inline styles for hotspots — arrow disc overlay on the panorama
const hotspotStyleTag = (
  <style>{`
    .pano-hotspot {
      width: 44px; height: 44px; border-radius: 50%;
      background: rgba(79,70,229,.85); color: #fff; font-weight: 700; font-size: 22px;
      display: flex; align-items: center; justify-content: center;
      border: 3px solid #fff; box-shadow: 0 4px 14px rgba(0,0,0,.4);
      cursor: pointer; transition: transform .15s, background .15s;
    }
    .pano-hotspot:hover { background: rgba(99,102,241,1); transform: scale(1.15); }
    /* POI markers (internal viewpoints) — pill labels floating in 3D space */
    .pano-vp-marker {
      color: #fff; font-weight: 600; font-size: 11px; line-height: 1;
      padding: 6px 10px; border-radius: 999px; border: 2px solid #fff;
      box-shadow: 0 4px 12px rgba(0,0,0,.35); cursor: pointer;
      display: inline-flex; align-items: center; gap: 4px;
      transition: transform .15s, box-shadow .15s, filter .15s;
      max-width: 180px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
    }
    .pano-vp-marker::before {
      content: ""; width: 6px; height: 6px; border-radius: 50%; background: #fff; box-shadow: 0 0 0 2px rgba(255,255,255,.5);
      flex-shrink: 0;
    }
    .pano-vp-marker:hover { transform: scale(1.08); filter: brightness(1.12); box-shadow: 0 6px 18px rgba(0,0,0,.45); }
    .pano-vp-marker span { display: inline-block; }
    @keyframes progress { from { width: 0%; } to { width: 100%; } }
  `}</style>
);

function PendingHotspotBar({ pendingHotspot, onCancel, onConfirm, panorami, saving }) {
  const [target, setTarget] = useState("");
  const [label, setLabel] = useState("");
  return (
    <div className="border-t bg-amber-50 p-3 flex items-center gap-2 flex-wrap" data-testid="pano-pending-hotspot">
      <MapPin className="h-4 w-4 text-amber-700" />
      <span className="text-sm text-amber-900">Nuovo hotspot @ yaw {pendingHotspot.yaw.toFixed(2)} pitch {pendingHotspot.pitch.toFixed(2)}</span>
      <select value={target} onChange={e => setTarget(e.target.value)} className="border rounded px-2 py-1 text-sm" data-testid="pano-hotspot-target">
        <option value="">-- Seleziona destinazione --</option>
        {panorami.map(p => <option key={p.id} value={p.id}>{p.nome}</option>)}
      </select>
      <Input placeholder="Etichetta (opzionale)" value={label} onChange={e => setLabel(e.target.value)} className="h-8 max-w-[200px] text-sm" data-testid="pano-hotspot-label" />
      <Button size="sm" onClick={() => target ? onConfirm(target, label) : toast.error("Scegli la destinazione")} disabled={saving || !target} data-testid="pano-hotspot-save">
        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Link2 className="h-4 w-4 mr-1" /> Salva</>}
      </Button>
      <Button size="sm" variant="ghost" onClick={onCancel} data-testid="pano-hotspot-cancel"><X className="h-4 w-4" /></Button>
    </div>
  );
}

const fileToBase64 = (file) => new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onload = (e) => resolve(e.target.result);
  r.onerror = reject;
  r.readAsDataURL(file);
});

export default function Panorami360({ authToken }) {
  const [panorami, setPanorami] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewing, setViewing] = useState(null);
  const [shareLinkCopied, setShareLinkCopied] = useState(false);
  const [qrOpen, setQrOpen] = useState(false);
  const [extractOpen, setExtractOpen] = useState(false);
  const [extractPreview, setExtractPreview] = useState(null);  // {dataUrl, mode: 'current'|'full360'}
  const [extractName, setExtractName] = useState("");
  const [extractSaving, setExtractSaving] = useState(false);
  const [tourOpen, setTourOpen] = useState(false);
  const [tourOrder, setTourOrder] = useState([]);  // ordered array of panorama ids
  const [tourDragIdx, setTourDragIdx] = useState(null);
  const [tourSaving, setTourSaving] = useState(false);
  const [tourStep, setTourStep] = useState(1);
  const [tourPreviewIdx, setTourPreviewIdx] = useState(0);
  const [tourPreviewFull, setTourPreviewFull] = useState({});
  const [tourPreviewLoading, setTourPreviewLoading] = useState(false);
  const [tourViewpoints, setTourViewpoints] = useState([]);  // [{yaw,pitch}] parallel to tourOrder
  const [tourName, setTourName] = useState("");
  const [tourId, setTourId] = useState(null);  // set when editing an existing tour
  const [tours, setTours] = useState([]);
  // Virtual tour (Matterport-style navigable player)
  const [vtourOpen, setVtourOpen] = useState(false);
  const [vtourData, setVtourData] = useState(null);        // the tour entity
  const [vtourPanos, setVtourPanos] = useState([]);        // full panorami with image_base64
  const [vtourLoading, setVtourLoading] = useState(false);
  const tourPreviewViewerRef = useRef(null);
  const viewerRef = useRef(null);

  // Carousel (slideshow) mode
  const [carouselOn, setCarouselOn] = useState(false);
  const [carouselIndex, setCarouselIndex] = useState(0);
  const [carouselDelay, setCarouselDelay] = useState(6000);
  // Auto-Walk Presentation (Feb 2026): 3 modes
  // - "carousel": cycle through all panorami (legacy)
  // - "poi": walk through POIs of the CURRENT scene only
  // - "combined": walk POIs of scene → jump via first hotspot → walk POIs → loop
  const [presentationMode, setPresentationMode] = useState("combined");
  const [autoPoiIndex, setAutoPoiIndex] = useState(0);
  const [autoStepLabel, setAutoStepLabel] = useState(""); // HUD label
  const [autoStepDetail, setAutoStepDetail] = useState(""); // HUD subtitle

  // Hotspot editor state
  const [editingHotspots, setEditingHotspots] = useState(false);
  const [pendingHotspot, setPendingHotspot] = useState(null); // {yaw, pitch}
  const [savingHotspots, setSavingHotspots] = useState(false);
  // Internal viewpoints (POI dentro la stessa scena 360, tipo Matterport)
  const [editingVps, setEditingVps] = useState(false);
  const [pendingVp, setPendingVp] = useState(null); // {yaw, pitch}
  const [savingVps, setSavingVps] = useState(false);
  const [vpLabelInput, setVpLabelInput] = useState("");
  const [vpColorInput, setVpColorInput] = useState("#4f46e5");
  const [autoOpenPoi, setAutoOpenPoi] = useState(false); // auto-enable POI edit when opening viewer from tour card
  const [autoOpenHotspot, setAutoOpenHotspot] = useState(false); // auto-enable Hotspot edit
  // 3D Walkable Viewer (Fase 3 - Depth AI parallax)
  const [walkableOpen, setWalkableOpen] = useState(false);
  // Inline rename inside tour wizard sequence list
  const [renamingIdx, setRenamingIdx] = useState(-1);
  const [renameValue, setRenameValue] = useState("");
  // "Walking illusion" - camera shift parameters for the currently pending POI
  const [vpStepX, setVpStepX] = useState(0); // -100..+100 = horizontal step
  const [vpStepZ, setVpStepZ] = useState(0); // -100..+100 = forward step (dolly)
  const [vpZoom, setVpZoom] = useState(50);  // 0..100 = FOV zoom level captured
  const [uploadDlg, setUploadDlg] = useState(false);
  const [generateDlg, setGenerateDlg] = useState(false);

  const [upForm, setUpForm] = useState({ nome: "", note: "", file: null, preview: null });
  const [genForm, setGenForm] = useState({ nome: "", prompt: "", source_image: null, source_preview: null });
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);

  const upFileRef = useRef(null);
  const genFileRef = useRef(null);

  const load = useCallback(async () => {
    if (!authToken) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/panorami?token=${authToken}`);
      setPanorami(r.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore caricamento panorami");
    } finally { setLoading(false); }
  }, [authToken]);

  useEffect(() => { load(); }, [load]);

  // Auto-Walk Presentation driver (Feb 2026):
  // Runs the current presentationMode on each tick.
  useEffect(() => {
    if (!carouselOn || !viewing) return;

    const pois = viewing.internal_viewpoints || [];
    const hotspots = viewing.hotspots || [];

    // MODE: POI walk within current scene
    if (presentationMode === "poi") {
      if (pois.length < 1) {
        setAutoStepLabel("Nessun POI in questa scena");
        setAutoStepDetail("Aggiungi POI per usare questa modalità");
        return;
      }
      const currentPoi = pois[autoPoiIndex % pois.length];
      setAutoStepLabel(currentPoi.label || `POI ${(autoPoiIndex % pois.length) + 1}`);
      setAutoStepDetail(`${(autoPoiIndex % pois.length) + 1} / ${pois.length} — ${viewing.nome || "Scena"}`);
      rotateToViewpoint(currentPoi);
      const t = setTimeout(() => setAutoPoiIndex(i => i + 1), carouselDelay);
      return () => clearTimeout(t);
    }

    // MODE: Combined (POIs of current scene, then hop via first hotspot, repeat)
    if (presentationMode === "combined") {
      if (pois.length > 0 && autoPoiIndex < pois.length) {
        const currentPoi = pois[autoPoiIndex];
        setAutoStepLabel(currentPoi.label || `POI ${autoPoiIndex + 1}`);
        setAutoStepDetail(`Scena "${viewing.nome || "?"}" · POI ${autoPoiIndex + 1}/${pois.length}`);
        rotateToViewpoint(currentPoi);
        const t = setTimeout(() => setAutoPoiIndex(i => i + 1), carouselDelay);
        return () => clearTimeout(t);
      }
      // POIs done → jump via first hotspot if present, else next panorama in the list
      if (hotspots.length > 0) {
        const hs = hotspots[0];
        setAutoStepLabel("Cambio scena…");
        setAutoStepDetail(`Vado a "${(panorami.find(p => p.id === hs.target_panorama_id) || {}).nome || "prossima"}"`);
        const t = setTimeout(async () => {
          try {
            const r = await axios.get(`${API}/panorami/${hs.target_panorama_id}?token=${authToken}`);
            setViewing(r.data);
            setAutoPoiIndex(0);
          } catch { /* silent */ }
        }, Math.min(2500, carouselDelay));
        return () => clearTimeout(t);
      }
      // No hotspot → cycle to next panorama in collection
      const currentPos = panorami.findIndex(p => p.id === viewing.id);
      const nextPos = (currentPos + 1) % Math.max(panorami.length, 1);
      if (panorami.length < 2) { setCarouselOn(false); return; }
      setAutoStepLabel("Prossima scena");
      setAutoStepDetail(panorami[nextPos].nome || `#${nextPos + 1}`);
      const t = setTimeout(async () => {
        try {
          const r = await axios.get(`${API}/panorami/${panorami[nextPos].id}?token=${authToken}`);
          setViewing(r.data);
          setAutoPoiIndex(0);
        } catch { /* silent */ }
      }, Math.min(2500, carouselDelay));
      return () => clearTimeout(t);
    }

    // MODE: legacy carousel (cycle all panorami)
    if (presentationMode === "carousel") {
      if (panorami.length < 2) return;
      setAutoStepLabel(viewing.nome || "Scena");
      setAutoStepDetail(`Scena ${carouselIndex + 1}/${panorami.length}`);
      const timer = setTimeout(async () => {
        const next = (carouselIndex + 1) % panorami.length;
        setCarouselIndex(next);
        const nextItem = panorami[next];
        try {
          const r = await axios.get(`${API}/panorami/${nextItem.id}?token=${authToken}`);
          setViewing(r.data);
        } catch { /* silently skip */ }
      }, carouselDelay);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [carouselOn, carouselIndex, viewing, panorami, carouselDelay, authToken, presentationMode, autoPoiIndex]);

  const startCarousel = async (mode = "combined") => {
    setPresentationMode(mode);
    if (mode === "carousel") {
      if (panorami.length < 2) { toast.error("Servono almeno 2 panorami per il carousel"); return; }
      setCarouselIndex(0);
      setCarouselOn(true);
      try {
        const r = await axios.get(`${API}/panorami/${panorami[0].id}?token=${authToken}`);
        setViewing(r.data);
      } catch { toast.error("Errore avvio carousel"); }
      return;
    }
    if (mode === "poi") {
      if (!viewing || (viewing.internal_viewpoints || []).length < 1) {
        toast.error("Nessun POI in questa scena — aggiungine almeno 1 prima di avviare la camminata"); return;
      }
      setAutoPoiIndex(0);
      setCarouselOn(true);
      return;
    }
    if (mode === "combined") {
      if (!viewing) { toast.error("Apri prima una scena"); return; }
      setAutoPoiIndex(0);
      setCarouselOn(true);
      return;
    }
  };
  const stopCarousel = () => { setCarouselOn(false); setAutoPoiIndex(0); setAutoStepLabel(""); setAutoStepDetail(""); };

  // Hotspot editor handlers
  const handleAddHotspot = async (targetPanoramaId, label) => {
    if (!pendingHotspot || !viewing || !targetPanoramaId) return;
    setSavingHotspots(true);
    const newHotspot = {
      id: `hs-${Date.now()}`,
      target_panorama_id: targetPanoramaId,
      yaw: pendingHotspot.yaw,
      pitch: pendingHotspot.pitch,
      label: label || "",
    };
    const updated = [...(viewing.hotspots || []), newHotspot];
    try {
      const r = await axios.put(`${API}/panorami/${viewing.id}/hotspots?token=${authToken}`, { hotspots: updated });
      setViewing(prev => ({ ...prev, hotspots: r.data.hotspots }));
      setPanorami(prev => prev.map(p => p.id === viewing.id ? { ...p, hotspots: r.data.hotspots } : p));
      setPendingHotspot(null);
      toast.success("Hotspot aggiunto");
    } catch (e) { toast.error(e.response?.data?.detail || "Errore salvataggio hotspot"); }
    finally { setSavingHotspots(false); }
  };
  const handleRemoveHotspot = async (hotspotId) => {
    if (!viewing) return;
    const updated = (viewing.hotspots || []).filter(h => h.id !== hotspotId);
    try {
      const r = await axios.put(`${API}/panorami/${viewing.id}/hotspots?token=${authToken}`, { hotspots: updated });
      setViewing(prev => ({ ...prev, hotspots: r.data.hotspots }));
      setPanorami(prev => prev.map(p => p.id === viewing.id ? { ...p, hotspots: r.data.hotspots } : p));
      toast.success("Hotspot rimosso");
    } catch (e) { toast.error("Errore rimozione hotspot"); }
  };

  // --- INTERNAL VIEWPOINTS (POI dentro la scena 360) ---
  const handleAddViewpoint = async (label, color = "#4f46e5") => {
    if (!pendingVp || !viewing) return;
    setSavingVps(true);
    const newVp = {
      id: `vp-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      label: (label || "Vista").slice(0, 80),
      yaw: pendingVp.yaw,
      pitch: pendingVp.pitch,
      color: color || "#4f46e5",
      zoom: Number(vpZoom) || 50,
      // Walking illusion offsets (normalized -1..+1)
      step_x: (Number(vpStepX) || 0) / 100,
      step_z: (Number(vpStepZ) || 0) / 100,
    };
    const updated = [...(viewing.internal_viewpoints || []), newVp];
    try {
      const r = await axios.put(`${API}/panorami/${viewing.id}/viewpoints?token=${authToken}`, { viewpoints: updated });
      setViewing(prev => ({ ...prev, internal_viewpoints: r.data.internal_viewpoints }));
      setPanorami(prev => prev.map(p => p.id === viewing.id ? { ...p, internal_viewpoints: r.data.internal_viewpoints } : p));
      setPendingVp(null);
      setVpLabelInput("");
      setVpStepX(0); setVpStepZ(0); setVpZoom(50);
      toast.success(`POI "${label}" salvato${(newVp.step_x || newVp.step_z) ? " con cammino virtuale" : ""}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Errore salvataggio vista"); }
    finally { setSavingVps(false); }
  };
  const handleRemoveViewpoint = async (vpId) => {
    if (!viewing) return;
    const updated = (viewing.internal_viewpoints || []).filter(v => v.id !== vpId);
    try {
      const r = await axios.put(`${API}/panorami/${viewing.id}/viewpoints?token=${authToken}`, { viewpoints: updated });
      setViewing(prev => ({ ...prev, internal_viewpoints: r.data.internal_viewpoints }));
      setPanorami(prev => prev.map(p => p.id === viewing.id ? { ...p, internal_viewpoints: r.data.internal_viewpoints } : p));
      toast.success("POI rimosso");
    } catch { toast.error("Errore rimozione vista"); }
  };
  /** Aggiungi viewpoint dalla posizione ATTUALE del viewer (rotazione + zoom correnti). */
  const captureVpFromCurrentView = () => {
    try {
      const v = viewerRef.current;
      const inst = v && (v.getViewer ? v.getViewer() : v);
      if (!inst || !inst.getPosition) { toast.error("Viewer non pronto"); return; }
      const pos = inst.getPosition();
      const currentZoom = inst.getZoomLevel ? Number(inst.getZoomLevel()) : 50;
      setPendingVp({ yaw: Number(pos.yaw || 0), pitch: Number(pos.pitch || 0) });
      setVpZoom(Math.round(currentZoom));
      setVpStepX(0); setVpStepZ(0);
      setVpLabelInput("");
    } catch { toast.error("Impossibile leggere posizione"); }
  };
  /** Ruota il viewer verso la vista salvata con effetto cinematografico (illusione di camminata).
   *  Combines rotation + zoom + optional step offset for a walk-in-place illusion. */
  const rotateToViewpoint = (vp) => {
    try {
      const v = viewerRef.current;
      const inst = v && (v.getViewer ? v.getViewer() : v);
      if (!inst || !inst.animate) return;
      // Base rotation
      const targetYaw = Number(vp.yaw) + (Number(vp.step_x || 0) * Math.PI / 8); // side-step adds up to ~22.5°
      const targetPitch = Number(vp.pitch) - (Number(vp.step_z || 0) * Math.PI / 16); // forward step adds a slight down-tilt
      const targetZoom = Number(vp.zoom || 50) + (Number(vp.step_z || 0) * 20); // forward = zoom in, back = zoom out
      inst.animate({
        yaw: targetYaw,
        pitch: targetPitch,
        zoom: Math.max(0, Math.min(100, targetZoom)),
        speed: "6rpm",
      });
    } catch { /* noop */ }
  };
  /** Preview live delle slider "cammino virtuale" prima di salvare. */
  const previewWalkOffset = (stepX, stepZ) => {
    if (!pendingVp) return;
    try {
      const v = viewerRef.current;
      const inst = v && (v.getViewer ? v.getViewer() : v);
      if (!inst || !inst.rotate) return;
      const y = Number(pendingVp.yaw) + (Number(stepX) / 100) * Math.PI / 8;
      const p = Number(pendingVp.pitch) - (Number(stepZ) / 100) * Math.PI / 16;
      const z = (Number(vpZoom) || 50) + (Number(stepZ) / 100) * 20;
      inst.rotate({ yaw: y, pitch: p });
      if (inst.zoom) inst.zoom(Math.max(0, Math.min(100, z)));
    } catch { /* noop */ }
  };
  const handleJumpToPanorama = async (targetId, startYaw = null, startPitch = null) => {
    setCarouselOn(false);
    try {
      const r = await axios.get(`${API}/panorami/${targetId}?token=${authToken}`);
      setViewing(r.data);
      // If the hotspot provides an arrival viewpoint, rotate the viewer to it after mount
      if (startYaw !== null && startYaw !== undefined) {
        setTimeout(() => {
          try {
            const viewer = viewerRef.current;
            const inst = viewer && (viewer.getViewer ? viewer.getViewer() : viewer);
            if (inst && typeof inst.rotate === "function") {
              inst.rotate({ yaw: Number(startYaw), pitch: Number(startPitch ?? 0) });
            } else if (inst && typeof inst.animate === "function") {
              inst.animate({ yaw: Number(startYaw), pitch: Number(startPitch ?? 0), speed: "3rpm" });
            }
          } catch { /* ignore */ }
        }, 350);
      }
    } catch { toast.error("Impossibile aprire il panorama collegato"); }
  };

  // ================== FRAME EXTRACTION ==================
  const extractCurrentFrame = () => {
    try {
      // Grab the WebGL canvas inside the main viewer
      const container = document.querySelector("[data-testid='pano-viewer-container']");
      const canvas = container ? container.querySelector("canvas") : document.querySelector(".psv-canvas-container canvas, .psv-container canvas");
      if (!canvas) { toast.error("Viewer non pronto"); return; }
      // For a proper capture on WebGL, we need preserveDrawingBuffer=true. If not, canvas may be blank.
      // Fallback: force a redraw and capture immediately.
      let dataUrl = "";
      try { dataUrl = canvas.toDataURL("image/jpeg", 0.88); } catch (_) { dataUrl = ""; }
      if (!dataUrl || dataUrl.length < 100) {
        toast.error("Impossibile catturare il frame (WebGL context). Riprova ruotando la vista.");
        return;
      }
      setExtractPreview({ dataUrl, mode: "current" });
      const base = (viewing?.nome || "Panorama").slice(0, 40);
      const stamp = new Date().toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
      setExtractName(`${base} · vista ${stamp}`);
    } catch (e) {
      toast.error("Errore estrazione frame: " + e.message);
    }
  };

  const extractFullPanorama = () => {
    // Save the full equirectangular 360 as a gallery item (source_type: 'panorama_export')
    if (!viewing?.image_base64) { toast.error("Immagine non disponibile"); return; }
    setExtractPreview({ dataUrl: viewing.image_base64, mode: "full360" });
    const base = (viewing?.nome || "Panorama").slice(0, 60);
    setExtractName(`${base} · 360° completo`);
  };

  const saveExtractedFrame = async () => {
    if (!extractPreview) return;
    if (!extractName.trim()) { toast.error("Nome obbligatorio"); return; }
    setExtractSaving(true);
    try {
      const is360 = extractPreview.mode === "full360";
      await axios.post(`${API}/design/gallery/upload?token=${authToken}`, {
        nome: extractName.trim(),
        image_base64: extractPreview.dataUrl,
        is_360: is360,
        source_type: is360 ? "panorama_export" : "frame_360",
        source_id: viewing?.id || "",
      });
      toast.success(`Salvato in Galleria AI (${is360 ? "360° equirect" : "vista 2D"})`);
      setExtractOpen(false);
      setExtractPreview(null);
      setExtractName("");
    } catch (e) {
      toast.error("Errore salvataggio: " + (e.response?.data?.detail || e.message));
    } finally { setExtractSaving(false); }
  };

  // TOUR editor: apply hotspots in sequence (each panorama links to the next in `tourOrder`)
  const openTour = () => {
    if (panorami.length < 2) { toast.error("Servono almeno 2 panorami per un tour"); return; }
    setTourId(null);
    const proposal = `Tour del ${new Date().toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" })} (${panorami.length} panorami)`;
    setTourName(proposal);
    setTourOrder(panorami.map(p => p.id));
    setTourViewpoints(panorami.map(() => ({ yaw: 0, pitch: 0 })));
    setTourStep(1);
    setTourPreviewIdx(0);
    setTourPreviewFull({});
    setTourOpen(true);
  };
  const moveTourItem = (from, to) => {
    if (from === to || to < 0 || to >= tourOrder.length) return;
    const next = [...tourOrder];
    const [item] = next.splice(from, 1);
    next.splice(to, 0, item);
    setTourOrder(next);
  };
  const removeTourItem = (idx) => {
    if (idx < 0 || idx >= tourOrder.length) return;
    const removed = tourOrder[idx];
    const next = tourOrder.filter((_, i) => i !== idx);
    setTourOrder(next);
    setTourPreviewIdx(prev => Math.max(0, Math.min(prev, next.length - 1)));
    // free the cached full image for the removed pano
    setTourPreviewFull(prev => {
      const c = { ...prev }; delete c[removed]; return c;
    });
    toast.success("Panorama rimosso dalla sequenza (non eliminato dalla libreria)");
  };
  const loadFullForPreview = useCallback(async (panoId) => {
    if (!panoId || tourPreviewFull[panoId]) return;
    setTourPreviewLoading(true);
    try {
      const r = await axios.get(`${API}/panorami/${panoId}?token=${authToken}`);
      setTourPreviewFull(prev => ({ ...prev, [panoId]: r.data.image_base64 || "" }));
    } catch { /* ignore */ }
    finally { setTourPreviewLoading(false); }
  }, [authToken, tourPreviewFull]);
  const applyTour = async () => {
    if (tourOrder.length < 2) return;
    if (!tourName.trim()) { toast.error("Inserisci un nome per il tour"); return; }
    setTourSaving(true);
    try {
      const normalizedVp = tourOrder.map((_, i) => tourViewpoints[i] || { yaw: 0, pitch: 0 });
      const body = {
        nome: tourName.trim(),
        panorama_ids: tourOrder,
        viewpoints: normalizedVp,
        apply_hotspots: true,
      };
      let saved;
      if (tourId) {
        const r = await axios.put(`${API}/tours-360/${tourId}?token=${authToken}`, body);
        saved = r.data;
        toast.success(`Tour "${saved.nome}" aggiornato`);
      } else {
        const r = await axios.post(`${API}/tours-360?token=${authToken}`, body);
        saved = r.data;
        toast.success(`Tour "${saved.nome}" salvato`);
      }
      await load();
      await loadTours();
      setTourOpen(false);
      setTourId(null);
    } catch (e) {
      toast.error("Errore salvataggio tour: " + (e.response?.data?.detail || e.message));
    } finally { setTourSaving(false); }
  };
  const loadTours = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/tours-360?token=${authToken}`);
      setTours(r.data || []);
    } catch { /* ignore */ }
  }, [authToken]);
  useEffect(() => { loadTours(); }, [loadTours]);
  const openTourForEdit = (t) => {
    // filter panorama_ids that still exist in the library
    const validIds = (t.panorama_ids || []).filter(pid => panorami.some(p => p.id === pid));
    if (validIds.length < 2) { toast.error("Il tour ha meno di 2 panorami disponibili"); return; }
    setTourId(t.id);
    setTourName(t.nome || "");
    setTourOrder(validIds);
    setTourViewpoints((t.viewpoints || []).slice(0, validIds.length).concat(
      Array.from({ length: Math.max(0, validIds.length - (t.viewpoints || []).length) }, () => ({ yaw: 0, pitch: 0 }))
    ));
    setTourStep(1);
    setTourPreviewIdx(0);
    setTourPreviewFull({});
    setTourOpen(true);
  };
  // Auto-enable POI edit mode when opening viewer via "POI su:" shortcut in tour cards
  useEffect(() => {
    if (viewing && autoOpenPoi) {
      setEditingVps(true);
      setEditingHotspots(false);
      setAutoOpenPoi(false);
    }
    if (viewing && autoOpenHotspot) {
      setEditingHotspots(true);
      setEditingVps(false);
      setAutoOpenHotspot(false);
    }
  }, [viewing, autoOpenPoi, autoOpenHotspot]);

  const deleteTour = async (t) => {
    if (!window.confirm(`Eliminare il tour "${t.nome}"? Gli hotspot direzionali collegati verranno rimossi ma i panorami restano.`)) return;
    try {
      await axios.delete(`${API}/tours-360/${t.id}?token=${authToken}`);
      await load(); await loadTours();
      toast.success("Tour eliminato");
    } catch { toast.error("Errore eliminazione tour"); }
  };

  const savePanoramaName = async (panoId, newName) => {
    const clean = (newName || "").trim();
    if (!clean) { setRenamingIdx(-1); return; }
    try {
      await axios.put(`${API}/panorami/${panoId}/rename?token=${authToken}`, { nome: clean });
      setPanorami(prev => prev.map(p => p.id === panoId ? { ...p, nome: clean } : p));
      if (viewing?.id === panoId) setViewing(prev => ({ ...prev, nome: clean }));
      toast.success(`Rinominato: "${clean}"`);
    } catch { toast.error("Errore rinomina panorama"); }
    finally { setRenamingIdx(-1); setRenameValue(""); }
  };

  /** Open a specific panorama with POI edit mode automatically enabled. */
  const openPanoramaForPoiEdit = async (panoId) => {
    try {
      const r = await axios.get(`${API}/panorami/${panoId}?token=${authToken}`);
      setViewing(r.data);
      setAutoOpenPoi(true);
    } catch { toast.error("Errore caricamento panorama"); }
  };

  /** Open a specific panorama with Hotspot edit mode automatically enabled. */
  const openPanoramaForHotspotEdit = async (panoId) => {
    try {
      const r = await axios.get(`${API}/panorami/${panoId}?token=${authToken}`);
      setViewing(r.data);
      setAutoOpenHotspot(true);
    } catch { toast.error("Errore caricamento panorama"); }
  };

  /** Fase 2 (Feb 2026): launch Matterport-style navigable virtual tour player */
  const launchVirtualTour = async (t) => {
    const ids = (t.panorama_ids || []).filter(pid => panorami.some(p => p.id === pid));
    if (ids.length < 2) { toast.error("Il tour necessita di almeno 2 panorami disponibili"); return; }
    setVtourLoading(true);
    setVtourData(t);
    try {
      // Load each panorama with full image_base64 (list endpoint ships only thumbnails)
      const fetched = await Promise.all(ids.map(async (pid) => {
        try {
          const r = await axios.get(`${API}/panorami/${pid}?token=${authToken}`);
          return r.data;
        } catch { return null; }
      }));
      const valid = fetched.filter(Boolean);
      if (valid.length < 2) {
        toast.error("Impossibile caricare abbastanza panorami del tour");
        setVtourLoading(false); setVtourData(null);
        return;
      }
      setVtourPanos(valid);
      setVtourOpen(true);
    } catch (e) {
      toast.error("Errore caricamento tour navigabile");
      setVtourData(null);
    } finally {
      setVtourLoading(false);
    }
  };
  const captureCurrentViewpoint = () => {
    try {
      const v = tourPreviewViewerRef.current;
      const inst = v && (v.getViewer ? v.getViewer() : v);
      if (!inst || !inst.getPosition) { toast.error("Viewer non pronto"); return; }
      const pos = inst.getPosition();
      const yaw = Number(pos.yaw || 0);
      const pitch = Number(pos.pitch || 0);
      setTourViewpoints(prev => {
        const arr = prev.slice();
        while (arr.length < tourOrder.length) arr.push({ yaw: 0, pitch: 0 });
        arr[tourPreviewIdx] = { yaw, pitch };
        return arr;
      });
      toast.success(`Punto di vista salvato (yaw ${yaw.toFixed(2)}, pitch ${pitch.toFixed(2)})`);
    } catch (e) { toast.error("Impossibile leggere la posizione del viewer"); }
  };
  const clearTourLinks = async () => {
    if (!window.confirm("Rimuovere tutti gli hotspot generati dal tour?")) return;
    setTourSaving(true);
    try {
      for (const pano of panorami) {
        const remaining = (pano.hotspots || []).filter(h => !String(h.id).startsWith("tour-seq-"));
        if (remaining.length !== (pano.hotspots || []).length) {
          await axios.put(`${API}/panorami/${pano.id}/hotspots?token=${authToken}`, { hotspots: remaining });
        }
      }
      await load();
      toast.success("Tour rimosso");
    } catch { toast.error("Errore rimozione tour"); }
    finally { setTourSaving(false); }
  };

  // Deep-link: ?panorama=<id> opens the viewer directly (e.g., shared VR link on smartphone)
  useEffect(() => {
    if (!panorami.length) return;
    const p = new URLSearchParams(window.location.search).get("panorama");
    if (p) {
      const found = panorami.find(x => x.id === p);
      if (found) {
        // list ship only thumbnail → fetch the full image before opening the viewer
        (async () => {
          try {
            const r = await axios.get(`${API}/panorami/${found.id}?token=${authToken}`);
            setViewing(r.data);
          } catch { setViewing(found); }
          window.history.replaceState({}, "", window.location.pathname);
        })();
      }
    }
  }, [panorami, authToken]);

  // Preload the full image for the currently previewed pano in the wizard step 2
  useEffect(() => {
    if (!tourOpen || tourStep !== 2) return;
    const pid = tourOrder[tourPreviewIdx];
    if (pid) loadFullForPreview(pid);
  }, [tourOpen, tourStep, tourPreviewIdx, tourOrder, loadFullForPreview]);

  // Deep-link: ?openTour=<tid> auto-opens the tour editor (used after batch → auto-open)
  useEffect(() => {
    if (!tours.length || !panorami.length) return;
    const tid = new URLSearchParams(window.location.search).get("openTour");
    if (!tid) return;
    const t = tours.find(x => x.id === tid);
    if (t) {
      openTourForEdit(t);
      window.history.replaceState({}, "", window.location.pathname);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tours, panorami]);

  // ------ Upload existing 360 photo ------
  const handleUpFile = async (f) => {
    if (!f) return;
    const b64 = await fileToBase64(f);
    setUpForm(prev => ({ ...prev, file: f, preview: b64 }));
  };
  const handleUpPaste = async () => {
    try {
      const items = await navigator.clipboard.read();
      for (const it of items) for (const t of it.types) {
        if (t.startsWith("image/")) {
          const blob = await it.getType(t);
          const b64 = await new Promise((res) => { const r = new FileReader(); r.onload = e => res(e.target.result); r.readAsDataURL(blob); });
          setUpForm(prev => ({ ...prev, file: { name: `clipboard.${t.split("/")[1]}` }, preview: b64 }));
          return;
        }
      }
      toast.error("Nessuna immagine negli appunti");
    } catch { toast.error("Impossibile leggere dagli appunti"); }
  };
  const submitUpload = async () => {
    if (!upForm.preview) { toast.error("Carica una foto 360°"); return; }
    if (!upForm.nome.trim()) { toast.error("Dai un nome al panorama"); return; }
    setSaving(true);
    try {
      const r = await axios.post(`${API}/panorami/upload?token=${authToken}`, {
        nome: upForm.nome, note: upForm.note, image_base64: upForm.preview,
      });
      setPanorami(prev => [r.data, ...prev]);
      setUploadDlg(false);
      setUpForm({ nome: "", note: "", file: null, preview: null });
      toast.success("Panorama caricato");
    } catch (e) { toast.error(e.response?.data?.detail || "Errore upload"); }
    finally { setSaving(false); }
  };

  // ------ Generate AI 360 ------
  const handleGenSource = async (f) => {
    if (!f) return;
    const b64 = await fileToBase64(f);
    setGenForm(prev => ({ ...prev, source_image: b64, source_preview: b64 }));
  };
  const submitGenerate = async () => {
    if (!genForm.prompt.trim() && !genForm.source_image) { toast.error("Inserisci un prompt o carica un'immagine sorgente"); return; }
    if (!genForm.nome.trim()) { toast.error("Dai un nome al panorama"); return; }
    setGenerating(true);
    try {
      const r = await axios.post(`${API}/panorami/generate?token=${authToken}`, {
        nome: genForm.nome, prompt: genForm.prompt, source_image: genForm.source_image,
      }, { timeout: 180000 });
      setPanorami(prev => [r.data, ...prev]);
      setGenerateDlg(false);
      setGenForm({ nome: "", prompt: "", source_image: null, source_preview: null });
      window.dispatchEvent(new Event("wallet:refresh"));
      toast.success("Panorama AI generato!");
    } catch (e) {
      const d = e.response?.data?.detail || e.message || "Errore generazione";
      if (e.response?.status === 402) { toast.error(d); window.dispatchEvent(new Event("wallet:open-recharge")); }
      else toast.error(d);
    } finally { setGenerating(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Eliminare questo panorama?")) return;
    try {
      await axios.delete(`${API}/panorami/${id}?token=${authToken}`);
      setPanorami(prev => prev.filter(p => p.id !== id));
      if (viewing?.id === id) setViewing(null);
      toast.success("Eliminato");
    } catch (e) { toast.error("Errore eliminazione"); }
  };

  return (
    <div className="space-y-4">
      {hotspotStyleTag}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Globe className="h-6 w-6 text-indigo-600" /> Panorami 360°
          </h2>
          <p className="text-sm text-gray-600">Immagini equirettangolari navigabili. Carica foto reali o generane di nuove con l&apos;AI.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={openTour} disabled={panorami.length < 2} data-testid="pano-tour-btn" title="Crea un tour navigabile collegando i panorami in sequenza">
            <Route className="h-4 w-4 mr-1" /> Tour
          </Button>
          <Button variant="outline" onClick={() => setUploadDlg(true)} data-testid="pano-upload-btn">
            <Upload className="h-4 w-4 mr-1" /> Carica foto 360°
          </Button>
          <Button onClick={() => setGenerateDlg(true)} className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700" data-testid="pano-generate-btn">
            <Wand2 className="h-4 w-4 mr-1" /> Genera con AI
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-indigo-600" /></div>
      ) : panorami.length === 0 ? (
        <Card className="border-dashed border-2">
          <CardContent className="py-12 text-center text-gray-500">
            <Globe className="h-12 w-12 mx-auto mb-3 text-gray-300" />
            <p className="text-lg font-medium">Nessun panorama ancora</p>
            <p className="text-sm">Carica una foto 360° o generane una con l&apos;AI per iniziare.</p>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* SAVED TOURS list */}
          {tours.length > 0 && (
            <Card className="mb-4 border-indigo-200" data-testid="pano-tours-list">
              <CardContent className="py-3">
                <div className="flex items-center gap-2 mb-2">
                  <Route className="h-4 w-4 text-indigo-600" />
                  <h3 className="text-sm font-semibold text-indigo-800">Tour salvati ({tours.length})</h3>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                  {tours.map(t => {
                    const thumbs = (t.panorama_ids || []).slice(0, 3).map(pid => panorami.find(p => p.id === pid)?.thumbnail).filter(Boolean);
                    const vpSaved = (t.viewpoints || []).filter(v => v && (v.yaw !== 0 || v.pitch !== 0)).length;
                    return (
                      <div key={t.id} className="border rounded-lg p-2 hover:bg-indigo-50 transition group" data-testid={`pano-tour-card-${t.id}`}>
                        <div className="flex items-center gap-2 mb-1">
                          <div className="flex -space-x-2 flex-shrink-0">
                            {thumbs.map((src, i) => (
                              <img key={i} src={src} alt="" className="w-10 h-6 object-cover rounded border-2 border-white" />
                            ))}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium truncate" title={t.nome}>{t.nome}</div>
                            <div className="text-[10px] text-gray-500">
                              {(t.panorama_ids || []).length} pano · {vpSaved} vp salvati · {new Date(t.updated_at).toLocaleDateString("it-IT")}
                            </div>
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-1">
                          <Button size="sm" variant="outline" className="text-xs h-7 min-w-[80px] flex-1" onClick={() => openTourForEdit(t)} data-testid={`pano-tour-edit-${t.id}`}>
                            <Wand2 className="h-3 w-3 mr-1" /> Modifica
                          </Button>
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button size="sm" variant="outline" className="text-xs h-7 border-purple-400 text-purple-700 hover:bg-purple-50" data-testid={`pano-tour-poi-${t.id}`} title="Aggiungi punti di vista (POI) dentro le foto 360° del tour">
                                <Sparkles className="h-3 w-3 mr-1" /> POI
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-72 p-2" data-testid={`pano-tour-poi-popover-${t.id}`}>
                              <div className="text-xs font-semibold text-purple-800 mb-2">Scegli su quale foto 360° del tour aggiungere i POI:</div>
                              <div className="space-y-1 max-h-64 overflow-y-auto">
                                {(t.panorama_ids || []).map((pid, idx) => {
                                  const p = panorami.find(x => x.id === pid);
                                  if (!p) return null;
                                  const cnt = (p.internal_viewpoints || []).length;
                                  return (
                                    <button
                                      key={pid}
                                      type="button"
                                      onClick={() => openPanoramaForPoiEdit(pid)}
                                      className="w-full flex items-center gap-2 p-2 rounded hover:bg-purple-50 border border-transparent hover:border-purple-200 text-left"
                                      data-testid={`pano-tour-poi-select-${pid}`}
                                    >
                                      <img src={p.thumbnail || ""} alt="" className="w-12 h-8 object-cover rounded flex-shrink-0" />
                                      <div className="flex-1 min-w-0">
                                        <div className="text-xs font-medium truncate">{idx + 1}. {p.nome || `Scena ${idx + 1}`}</div>
                                        <div className="text-[10px] text-gray-500">
                                          {cnt > 0 ? <span className="text-purple-700 font-semibold">{cnt} POI salvati</span> : "Nessun POI ancora"}
                                        </div>
                                      </div>
                                      <Sparkles className="h-3.5 w-3.5 text-purple-500 flex-shrink-0" />
                                    </button>
                                  );
                                })}
                              </div>
                              <div className="text-[10px] text-gray-500 mt-2 pt-2 border-t">
                                Il viewer si apre con la modalità <strong>POI Vista ON</strong>. Ruota, clicca sulla scena o usa &quot;Cattura vista corrente&quot;.
                              </div>
                            </PopoverContent>
                          </Popover>
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button size="sm" variant="outline" className="text-xs h-7 border-amber-400 text-amber-700 hover:bg-amber-50" data-testid={`pano-tour-hs-${t.id}`} title="Collega più foto 360° con hotspot direzionali (frecce che portano ad un altra scena)">
                                <MapPin className="h-3 w-3 mr-1" /> Hotspot
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-72 p-2" data-testid={`pano-tour-hs-popover-${t.id}`}>
                              <div className="text-xs font-semibold text-amber-800 mb-2">Scegli il panorama di partenza — poi cliccherai sulla scena per posizionare l&apos;hotspot che porta a un altro panorama:</div>
                              <div className="space-y-1 max-h-64 overflow-y-auto">
                                {(t.panorama_ids || []).map((pid, idx) => {
                                  const p = panorami.find(x => x.id === pid);
                                  if (!p) return null;
                                  const cnt = (p.hotspots || []).length;
                                  return (
                                    <button
                                      key={pid}
                                      type="button"
                                      onClick={() => openPanoramaForHotspotEdit(pid)}
                                      className="w-full flex items-center gap-2 p-2 rounded hover:bg-amber-50 border border-transparent hover:border-amber-200 text-left"
                                      data-testid={`pano-tour-hs-select-${pid}`}
                                    >
                                      <img src={p.thumbnail || ""} alt="" className="w-12 h-8 object-cover rounded flex-shrink-0" />
                                      <div className="flex-1 min-w-0">
                                        <div className="text-xs font-medium truncate">{idx + 1}. {p.nome || `Scena ${idx + 1}`}</div>
                                        <div className="text-[10px] text-gray-500">
                                          {cnt > 0 ? <span className="text-amber-700 font-semibold">{cnt} hotspot già presenti</span> : "Nessun hotspot ancora"}
                                        </div>
                                      </div>
                                      <MapPin className="h-3.5 w-3.5 text-amber-500 flex-shrink-0" />
                                    </button>
                                  );
                                })}
                              </div>
                              <div className="text-[10px] text-gray-500 mt-2 pt-2 border-t">
                                Il viewer si apre con <strong>Hotspot ON</strong>. Clicca un punto della scena → scegli la destinazione (altro panorama).
                              </div>
                            </PopoverContent>
                          </Popover>
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button size="sm" variant="outline" className="text-xs h-7 border-emerald-500 text-emerald-700 hover:bg-emerald-50" data-testid={`pano-tour-export-${t.id}`} title="Esporta il tour in vari formati">
                                <Download className="h-3 w-3 mr-1" /> Esporta ▾
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-64 p-2" data-testid={`pano-tour-export-popover-${t.id}`}>
                              <div className="text-xs font-semibold text-emerald-800 mb-2">Formato export</div>
                              <div className="space-y-1">
                                <button type="button" onClick={async () => {
                                  try {
                                    const res = await axios.get(`${API}/tours-360/${t.id}/export-html?token=${authToken}`, { responseType: "blob" });
                                    const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/html" }));
                                    const a = document.createElement("a");
                                    a.href = url; a.download = `tour_${(t.nome || 'tour').replace(/\s+/g,'_')}.html`;
                                    document.body.appendChild(a); a.click(); a.remove();
                                    window.URL.revokeObjectURL(url);
                                    toast.success("Tour HTML interattivo scaricato");
                                  } catch { toast.error("Errore export HTML"); }
                                }} className="w-full text-left p-2 rounded hover:bg-emerald-50 border border-emerald-200 bg-white" data-testid={`pano-tour-export-html-${t.id}`}>
                                  <div className="text-xs font-semibold text-emerald-900">🌐 HTML interattivo</div>
                                  <div className="text-[10px] text-emerald-700">Tour navigabile con POI + hotspot, apre in browser</div>
                                </button>
                                <button type="button" onClick={async () => {
                                  try {
                                    const res = await axios.get(`${API}/tours-360/${t.id}/export-jpg-zip?token=${authToken}`, { responseType: "blob" });
                                    const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/zip" }));
                                    const a = document.createElement("a");
                                    a.href = url; a.download = `tour_${(t.nome || 'tour').replace(/\s+/g,'_')}_360jpg.zip`;
                                    document.body.appendChild(a); a.click(); a.remove();
                                    window.URL.revokeObjectURL(url);
                                    toast.success("ZIP con panorami 360.jpg scaricato");
                                  } catch { toast.error("Errore export ZIP"); }
                                }} className="w-full text-left p-2 rounded hover:bg-blue-50 border border-blue-200 bg-white" data-testid={`pano-tour-export-zip-${t.id}`}>
                                  <div className="text-xs font-semibold text-blue-900">📦 ZIP di 360°.jpg</div>
                                  <div className="text-[10px] text-blue-700">Tutti i panorami come JPG + manifest.json (usabili in altri viewer 360)</div>
                                </button>
                              </div>
                            </PopoverContent>
                          </Popover>
                          <Button size="sm" className="text-xs h-7 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white border-0" onClick={() => launchVirtualTour(t)} data-testid={`pano-tour-vtour-${t.id}`} title="Virtual Tour navigabile stile Matterport (frecce 3D, mini-mappa, gallery)" disabled={vtourLoading}>
                            {vtourLoading && vtourData?.id === t.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <><Globe className="h-3 w-3 mr-1" /> VR Tour</>}
                          </Button>
                          <Button size="sm" variant="outline" className="text-xs h-7" onClick={() => {
                            const firstId = (t.panorama_ids || [])[0];
                            const p = panorami.find(x => x.id === firstId);
                            if (p) {
                              (async () => {
                                try {
                                  const r = await axios.get(`${API}/panorami/${p.id}?token=${authToken}`);
                                  setViewing(r.data);
                                } catch { setViewing(p); }
                              })();
                            }
                          }} data-testid={`pano-tour-play-${t.id}`} title="Avvia il tour dal primo panorama (modalità classica hotspot)">
                            <Play className="h-3 w-3" />
                          </Button>
                          <Button size="sm" variant="outline" className="text-xs h-7 text-red-600 border-red-200 hover:bg-red-50" onClick={() => deleteTour(t)} data-testid={`pano-tour-del-${t.id}`}>
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="pano-grid">
          {panorami.map(p => (
            <Card key={p.id} className="overflow-hidden hover:shadow-lg transition-shadow" data-testid={`pano-card-${p.id}`}>
              <div className="aspect-[2/1] bg-slate-100 relative cursor-pointer" onClick={async () => {
                try {
                  // Lazy-load the full image on demand (list endpoint only ships the thumbnail)
                  if (p.image_base64) {
                    setViewing(p);
                  } else {
                    const r = await axios.get(`${API}/panorami/${p.id}?token=${authToken}`);
                    setViewing(r.data);
                  }
                } catch (e) {
                  toast.error("Impossibile caricare il panorama");
                }
              }}>
                <img src={p.thumbnail || p.image_base64} alt={p.nome} className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-black/0 hover:bg-black/40 flex items-center justify-center transition-colors">
                  <Eye className="h-8 w-8 text-white opacity-0 hover:opacity-100 transition-opacity" />
                </div>
                {p.is_ai_generated && (
                  <Badge className="absolute top-2 left-2 bg-purple-600 text-white text-[10px]">
                    <Sparkles className="h-3 w-3 mr-1" /> AI
                  </Badge>
                )}
              </div>
              <CardContent className="p-3">
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-sm truncate" title={p.nome}>{p.nome}</div>
                    <div className="text-xs text-gray-500 truncate">{new Date(p.created_at).toLocaleDateString("it-IT")}</div>
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => handleDelete(p.id)} className="text-red-500 hover:text-red-700 hover:bg-red-50" data-testid={`pano-delete-${p.id}`}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
                {p.is_ai_generated && p.prompt && (
                  <div className="mt-2 text-xs text-gray-600 italic line-clamp-2">&ldquo;{p.prompt}&rdquo;</div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
        </>
      )}

      {/* Viewer Dialog */}
      <Dialog open={!!viewing} onOpenChange={(o) => {
        if (!o) {
          setViewing(null);
          setCarouselOn(false);
          setEditingHotspots(false);
          setPendingHotspot(null);
          setEditingVps(false);
          setPendingVp(null);
          setAutoOpenPoi(false);
          setAutoOpenHotspot(false);
        }
      }}>
        <DialogContent className="max-w-6xl w-full h-[90vh] p-0 flex flex-col" data-testid="pano-viewer">
          <DialogHeader className="p-3 border-b flex-shrink-0">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <DialogTitle className="flex items-center gap-2">
                <Globe className="h-5 w-5 text-indigo-600" /> {viewing?.nome || ""}
                {viewing?.is_ai_generated && <Badge className="bg-purple-600 text-white text-[10px]"><Sparkles className="h-3 w-3 mr-1" /> AI</Badge>}
                {carouselOn && <Badge className="bg-emerald-600 text-white text-[10px] animate-pulse">
                  {presentationMode === "poi" ? "🚶 POI WALK" : presentationMode === "combined" ? "🎬 TOUR" : `CAROUSEL ${carouselIndex+1}/${panorami.length}`}
                </Badge>}
                {(viewing?.hotspots || []).length > 0 && <Badge variant="outline" className="text-[10px]"><MapPin className="h-3 w-3 mr-1" /> {viewing.hotspots.length} link</Badge>}
              </DialogTitle>
              <div className="flex gap-1.5 flex-wrap">
                <Button size="sm" variant={editingHotspots ? "default" : "outline"} onClick={() => { setEditingHotspots(v => !v); setPendingHotspot(null); if (!editingHotspots) setEditingVps(false); }}
                  className={editingHotspots ? "bg-amber-600 hover:bg-amber-700 text-white" : ""}
                  data-testid="pano-edit-hotspots-btn" title="Aggiungi/rimuovi hotspot navigabili tra scene diverse">
                  <MapPin className="h-4 w-4 mr-1" /> {editingHotspots ? "Editing ON" : "Hotspot"}
                </Button>
                <Button size="sm" variant={editingVps ? "default" : "outline"} onClick={() => { setEditingVps(v => !v); setPendingVp(null); if (!editingVps) setEditingHotspots(false); }}
                  className={editingVps ? "bg-purple-600 hover:bg-purple-700 text-white" : "border-purple-400 text-purple-700 hover:bg-purple-50"}
                  data-testid="pano-edit-vps-btn" title="Aggiungi/rimuovi punti di vista dentro questa scena (Matterport-style POI)">
                  <Sparkles className="h-4 w-4 mr-1" /> {editingVps ? "POI ON" : "POI Vista"}
                  {(viewing?.internal_viewpoints || []).length > 0 && <Badge variant="outline" className="ml-1 text-[10px]">{viewing.internal_viewpoints.length}</Badge>}
                </Button>
                <Button size="sm" variant="outline" onClick={() => setWalkableOpen(true)}
                  className="border-yellow-500 text-yellow-800 bg-yellow-50 hover:bg-yellow-100"
                  data-testid="pano-walk-btn" title="Modalità 3D Walk — muoviti fisicamente nella scena con parallax reale (Depth AI)">
                  <Zap className="h-4 w-4 mr-1" /> 3D Walk
                  {viewing?.depth_map_base64 && <Badge variant="outline" className="ml-1 text-[10px] bg-emerald-100">✓</Badge>}
                </Button>
                {!carouselOn ? (
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button size="sm" variant="outline" data-testid="pano-carousel-play-btn" title="Presentazione automatica (POI + hotspot)" className="border-emerald-400 text-emerald-700 hover:bg-emerald-50">
                        <Play className="h-4 w-4 mr-1" /> Presentazione ▾
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-80 p-3" data-testid="pano-carousel-popover">
                      <div className="text-xs font-semibold text-emerald-800 mb-2">Modalità presentazione automatica</div>
                      <div className="space-y-1 mb-3">
                        <button type="button" onClick={() => { startCarousel("poi"); }}
                          disabled={(viewing?.internal_viewpoints || []).length < 1}
                          className="w-full text-left p-2 rounded border border-purple-200 bg-purple-50 hover:bg-purple-100 disabled:opacity-50 disabled:cursor-not-allowed"
                          data-testid="pano-carousel-mode-poi">
                          <div className="text-xs font-semibold text-purple-900 flex items-center justify-between">
                            <span>🚶 Cammina POI di questa scena</span>
                            <Badge variant="outline" className="text-[10px]">{(viewing?.internal_viewpoints || []).length}</Badge>
                          </div>
                          <div className="text-[10px] text-purple-700 mt-0.5">Ruota tra i POI della scena corrente in loop, con effetto cammino virtuale.</div>
                        </button>
                        <button type="button" onClick={() => { startCarousel("combined"); }}
                          className="w-full text-left p-2 rounded border border-emerald-200 bg-emerald-50 hover:bg-emerald-100"
                          data-testid="pano-carousel-mode-combined">
                          <div className="text-xs font-semibold text-emerald-900">🎬 Tour completo (POI + Hotspot)</div>
                          <div className="text-[10px] text-emerald-700 mt-0.5">Cammina i POI della scena → salta al prossimo panorama via hotspot → ripeti.</div>
                        </button>
                        <button type="button" onClick={() => { startCarousel("carousel"); }}
                          disabled={panorami.length < 2}
                          className="w-full text-left p-2 rounded border border-slate-200 bg-slate-50 hover:bg-slate-100 disabled:opacity-50"
                          data-testid="pano-carousel-mode-classic">
                          <div className="text-xs font-semibold text-slate-900">🔁 Carousel classico</div>
                          <div className="text-[10px] text-slate-700 mt-0.5">Cicla in sequenza tutti i {panorami.length} panorami salvati.</div>
                        </button>
                      </div>
                      <div>
                        <label className="text-[11px] font-semibold text-gray-700 flex items-center justify-between">
                          <span>Tempo per POI/scena</span>
                          <span className="font-mono">{(carouselDelay / 1000).toFixed(1)}s</span>
                        </label>
                        <input type="range" min="2000" max="10000" step="500" value={carouselDelay}
                          onChange={e => setCarouselDelay(Number(e.target.value))}
                          className="w-full accent-emerald-600" data-testid="pano-carousel-dwell" />
                        <div className="text-[9px] text-gray-500 flex justify-between"><span>2s (veloce)</span><span>10s (lento, cinematico)</span></div>
                      </div>
                    </PopoverContent>
                  </Popover>
                ) : (
                  <Button size="sm" variant="outline" onClick={stopCarousel} data-testid="pano-carousel-stop-btn" className="border-red-500 text-red-700 bg-red-50 hover:bg-red-100 animate-pulse">
                    <Pause className="h-4 w-4 mr-1" /> Stop Presentazione
                  </Button>
                )}
                <Button size="sm" variant="outline" onClick={() => {
                  const plugin = viewerRef.current?.getPlugin?.(StereoPlugin);
                  if (plugin) plugin.toggle();
                  else toast.error("VR non disponibile");
                }} data-testid="pano-vr-btn" title="Modalità VR / Cardboard">
                  <Smartphone className="h-4 w-4 mr-1" /> VR
                </Button>
                <Button size="sm" variant="outline" onClick={async () => {
                  const url = `${window.location.origin}${window.location.pathname}?panorama=${viewing.id}`;
                  try {
                    await navigator.clipboard.writeText(url);
                    setShareLinkCopied(true);
                    setTimeout(() => setShareLinkCopied(false), 2000);
                    toast.success("Link copiato — apri su smartphone per VR");
                  } catch { toast.error("Impossibile copiare"); }
                }} data-testid="pano-share-btn" title="Copia link VR">
                  {shareLinkCopied ? <Check className="h-4 w-4 mr-1 text-emerald-600" /> : <Share2 className="h-4 w-4 mr-1" />}
                  Condividi
                </Button>
                <Button size="sm" variant="outline" onClick={() => setQrOpen(true)} data-testid="pano-qr-btn" title="Mostra QR code per aprire su smartphone">
                  <QrCode className="h-4 w-4 mr-1" /> QR
                </Button>
                <Button size="sm" variant="outline" onClick={() => setExtractOpen(true)} data-testid="pano-extract-btn" title="Estrai una foto dalla vista corrente"
                  className="border-emerald-400 text-emerald-700 hover:bg-emerald-50">
                  <Camera className="h-4 w-4 mr-1" /> Estrai foto
                </Button>
                <Button size="sm" variant="outline" onClick={async () => {
                  try {
                    const res = await axios.get(`${API}/panorami/${viewing.id}/export-jpg?token=${authToken}`, { responseType: "blob" });
                    const url = window.URL.createObjectURL(new Blob([res.data], { type: "image/jpeg" }));
                    const a = document.createElement("a");
                    a.href = url; a.download = `${(viewing.nome || 'panorama').replace(/\s+/g,'_')}_360.jpg`;
                    document.body.appendChild(a); a.click(); a.remove();
                    window.URL.revokeObjectURL(url);
                    toast.success("Panorama 360° scaricato");
                  } catch { toast.error("Errore download 360.jpg"); }
                }} data-testid="pano-download-jpg" title="Scarica il panorama 360° come JPG equirettangolare"
                  className="border-blue-400 text-blue-700 hover:bg-blue-50">
                  <Download className="h-4 w-4 mr-1" /> Scarica 360.jpg
                </Button>
              </div>
            </div>
            {editingHotspots && (
              <div className="mt-2 p-2 rounded bg-amber-50 border border-amber-200 text-xs text-amber-900">
                <strong>Modalità hotspot:</strong> clicca su un punto del panorama per posizionare un nuovo hotspot; verrà chiesta la destinazione. Clicca su un hotspot esistente per rimuoverlo.
              </div>
            )}
            {editingVps && (
              <div className="mt-2 p-2 rounded bg-purple-50 border border-purple-200 text-xs text-purple-900 flex items-center justify-between gap-2 flex-wrap">
                <div>
                  <strong>Modalità POI Vista:</strong> clicca un punto della scena per aggiungere un punto di vista con nome. Oppure ruota la scena e cattura la vista corrente. Clicca su un POI esistente per rimuoverlo.
                </div>
                <Button size="sm" onClick={captureVpFromCurrentView} className="bg-purple-600 hover:bg-purple-700 text-white h-7 text-xs" data-testid="pano-capture-current-vp">
                  <Camera className="h-3 w-3 mr-1" /> Cattura vista corrente
                </Button>
              </div>
            )}
          </DialogHeader>
          <div className="flex-1 bg-black relative overflow-hidden" data-testid="pano-viewer-container">
            {viewing && viewing.image_base64 && (              <ReactPhotoSphereViewer
                key={viewing.id}
                ref={viewerRef}
                src={viewing.image_base64}
                width="100%"
                height="100%"
                container="pano-container"
                loadingImg={undefined}
                defaultZoomLvl={0}
                minFov={30}
                maxFov={110}
                fisheye={false}
                rendererParameters={{ preserveDrawingBuffer: true }}
                navbar={["autorotate", "zoom", "move", "gyroscope", "stereo", "fullscreen"]}
                plugins={[
                  [GyroscopePlugin, { touchmove: true, absolutePosition: false, moveMode: "smooth" }],
                  [StereoPlugin, {}],
                  [MarkersPlugin, {
                    markers: [
                      ...(viewing.hotspots || []).map(h => ({
                        id: h.id,
                        position: { yaw: h.yaw, pitch: h.pitch },
                        html: `<div class="pano-hotspot" title="${(h.label || 'Vai').replace(/"/g,'&quot;')}">→</div>`,
                        anchor: "center center",
                        data: { kind: "hotspot", target: h.target_panorama_id, label: h.label, start_yaw: h.start_yaw, start_pitch: h.start_pitch },
                      })),
                      ...(viewing.internal_viewpoints || []).map(v => ({
                        id: v.id,
                        position: { yaw: v.yaw, pitch: v.pitch },
                        html: `<div class="pano-vp-marker" style="background:${v.color || '#4f46e5'};" title="${(v.label || 'Vista').replace(/"/g,'&quot;')}"><span>${(v.label || '•').slice(0,14)}</span></div>`,
                        anchor: "center center",
                        data: { kind: "viewpoint", label: v.label, yaw: v.yaw, pitch: v.pitch },
                      })),
                    ],
                  }],
                ]}
                onReady={(instance) => {
                  const mp = instance.getPlugin(MarkersPlugin);
                  if (!mp) return;
                  mp.addEventListener("select-marker", ({ marker }) => {
                    const kind = marker.data?.kind || (marker.data?.target ? "hotspot" : "viewpoint");
                    if (kind === "hotspot") {
                      if (editingHotspots) {
                        if (window.confirm(`Rimuovere l'hotspot "${marker.data?.label || 'senza etichetta'}"?`)) {
                          handleRemoveHotspot(marker.id);
                        }
                      } else if (marker.data?.target) {
                        handleJumpToPanorama(marker.data.target, marker.data.start_yaw, marker.data.start_pitch);
                      }
                    } else if (kind === "viewpoint") {
                      if (editingVps) {
                        if (window.confirm(`Rimuovere il POI "${marker.data?.label || 'vista'}"?`)) {
                          handleRemoveViewpoint(marker.id);
                        }
                      } else {
                        rotateToViewpoint({ yaw: marker.data.yaw, pitch: marker.data.pitch });
                      }
                    }
                  });
                  instance.addEventListener("click", ({ data }) => {
                    if (data?.marker) return;
                    if (editingHotspots) {
                      setPendingHotspot({ yaw: data.yaw, pitch: data.pitch });
                    } else if (editingVps) {
                      setPendingVp({ yaw: data.yaw, pitch: data.pitch });
                    }
                  });
                }}
              />
            )}
            {/* Auto-Walk Presentation HUD */}
            {carouselOn && (autoStepLabel || autoStepDetail) && (
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-10 max-w-[80%]" data-testid="pano-autowalk-hud">
                <div className="bg-black/70 backdrop-blur border border-white/20 rounded-lg px-4 py-2 text-white shadow-2xl">
                  <div className="text-xs font-semibold flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                    {autoStepLabel}
                  </div>
                  {autoStepDetail && <div className="text-[10px] text-white/70 mt-0.5">{autoStepDetail}</div>}
                  <div className="mt-1 h-0.5 bg-white/20 rounded overflow-hidden">
                    <div className="h-full bg-emerald-400 animate-[progress_var(--dwell)_linear]"
                      style={{ "--dwell": `${carouselDelay}ms`, animation: `progress ${carouselDelay}ms linear` }} />
                  </div>
                </div>
              </div>
            )}
          </div>
          {/* Pending hotspot dialog inline */}
          {pendingHotspot && editingHotspots && (
            <PendingHotspotBar
              pendingHotspot={pendingHotspot}
              onCancel={() => setPendingHotspot(null)}
              onConfirm={(target, label) => handleAddHotspot(target, label)}
              panorami={panorami.filter(p => p.id !== viewing.id)}
              saving={savingHotspots}
            />
          )}
          {/* Pending viewpoint (POI) dialog inline — with walk simulator sliders */}
          {pendingVp && editingVps && (
            <div className="p-3 border-t bg-purple-50 space-y-2" data-testid="pano-vp-pending">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs text-purple-900 font-medium">
                  Nuovo POI @ yaw {pendingVp.yaw.toFixed(2)} · pitch {pendingVp.pitch.toFixed(2)}
                </span>
                <Input
                  value={vpLabelInput}
                  onChange={e => setVpLabelInput(e.target.value)}
                  placeholder="Nome POI (es. Cassa, Vetrina, Ingresso...)"
                  className="max-w-xs h-8 text-sm bg-white"
                  data-testid="pano-vp-label-input"
                  onKeyDown={(e) => { if (e.key === "Enter" && vpLabelInput.trim()) handleAddViewpoint(vpLabelInput.trim(), vpColorInput); }}
                  autoFocus
                />
                <input
                  type="color"
                  value={vpColorInput}
                  onChange={e => setVpColorInput(e.target.value)}
                  className="w-8 h-8 rounded border cursor-pointer"
                  title="Colore marker POI"
                  data-testid="pano-vp-color"
                />
              </div>
              {/* Walking illusion sliders — cinematographic camera shift */}
              <div className="bg-white/70 rounded p-2 border border-purple-200">
                <div className="text-[11px] font-semibold text-purple-800 mb-1 flex items-center gap-1">
                  🚶 Cammino virtuale <span className="text-[10px] font-normal text-purple-600">(simulazione spostamento dentro la stessa foto)</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-[11px]">
                  <label className="flex flex-col gap-0.5">
                    <span className="text-purple-700">← Passo laterale → <span className="font-mono text-purple-900">{vpStepX > 0 ? `+${vpStepX}` : vpStepX}</span></span>
                    <input type="range" min="-100" max="100" step="5" value={vpStepX}
                      onChange={e => { const nv = Number(e.target.value); setVpStepX(nv); previewWalkOffset(nv, vpStepZ); }}
                      className="accent-purple-600" data-testid="pano-vp-stepx" />
                  </label>
                  <label className="flex flex-col gap-0.5">
                    <span className="text-purple-700">↑ Avanti/Indietro ↓ <span className="font-mono text-purple-900">{vpStepZ > 0 ? `+${vpStepZ}` : vpStepZ}</span></span>
                    <input type="range" min="-100" max="100" step="5" value={vpStepZ}
                      onChange={e => { const nv = Number(e.target.value); setVpStepZ(nv); previewWalkOffset(vpStepX, nv); }}
                      className="accent-purple-600" data-testid="pano-vp-stepz" />
                  </label>
                  <label className="flex flex-col gap-0.5">
                    <span className="text-purple-700">Zoom (FOV) <span className="font-mono text-purple-900">{vpZoom}</span></span>
                    <input type="range" min="0" max="100" step="5" value={vpZoom}
                      onChange={e => { const nv = Number(e.target.value); setVpZoom(nv); try { const inst = viewerRef.current?.getViewer?.() || viewerRef.current; if (inst?.zoom) inst.zoom(nv); } catch (_) { /* noop */ } }}
                      className="accent-purple-600" data-testid="pano-vp-zoom" />
                  </label>
                </div>
                <div className="text-[10px] text-purple-700/80 mt-1">
                  Trascina gli slider per vedere in tempo reale come si sposterà la camera quando cliccherai questo POI. Combina laterale + avanti per un effetto di camminata diagonale.
                </div>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <Button size="sm" onClick={() => handleAddViewpoint(vpLabelInput.trim() || "Vista", vpColorInput)} disabled={savingVps} className="bg-purple-600 hover:bg-purple-700 text-white h-8" data-testid="pano-vp-save">
                  {savingVps ? <Loader2 className="h-3 w-3 animate-spin" /> : <><Check className="h-3 w-3 mr-1" /> Salva POI</>}
                </Button>
                <Button size="sm" variant="outline" onClick={() => { setPendingVp(null); setVpLabelInput(""); setVpStepX(0); setVpStepZ(0); setVpZoom(50); }} className="h-8">Annulla</Button>
                {(vpStepX !== 0 || vpStepZ !== 0) && (
                  <Button size="sm" variant="ghost" onClick={() => { setVpStepX(0); setVpStepZ(0); previewWalkOffset(0, 0); }} className="h-8 text-purple-700" data-testid="pano-vp-reset-walk">
                    Reset cammino
                  </Button>
                )}
              </div>
            </div>
          )}
          {/* Viewpoint list (only when edit ON) */}
          {editingVps && !pendingVp && (viewing?.internal_viewpoints || []).length > 0 && (
            <div className="p-3 border-t bg-purple-50/50 max-h-32 overflow-y-auto" data-testid="pano-vp-list">
              <div className="text-[11px] font-semibold text-purple-800 mb-2">POI salvati ({viewing.internal_viewpoints.length}) — clicca per andare, X per rimuovere</div>
              <div className="flex flex-wrap gap-2">
                {viewing.internal_viewpoints.map(vp => (
                  <div key={vp.id} className="flex items-center gap-1 bg-white border border-purple-200 rounded-full pl-2 pr-1 py-0.5 text-xs" data-testid={`pano-vp-chip-${vp.id}`}>
                    <button type="button" onClick={() => rotateToViewpoint(vp)} className="flex items-center gap-1.5 hover:text-purple-700">
                      <span className="w-2.5 h-2.5 rounded-full inline-block" style={{ backgroundColor: vp.color || "#4f46e5" }} />
                      {vp.label}
                    </button>
                    <button type="button" onClick={() => handleRemoveViewpoint(vp.id)} className="ml-1 w-5 h-5 rounded-full text-red-600 hover:bg-red-100 flex items-center justify-center" data-testid={`pano-vp-del-${vp.id}`} title="Rimuovi">
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="px-4 py-2 border-t bg-slate-50 text-[11px] text-slate-600 flex items-center gap-2">
            <Smartphone className="h-3.5 w-3.5 text-indigo-500" />
            <span>VR: apri il link condiviso su smartphone → tocca <strong>VR</strong> → visore Cardboard. <strong>Hotspot</strong>: click su un link per navigare tra panorami.</span>
          </div>
        </DialogContent>
      </Dialog>

      {/* Upload Dialog */}
      <Dialog open={uploadDlg} onOpenChange={setUploadDlg}>
        <DialogContent className="max-w-2xl" data-testid="pano-upload-dialog">
          <DialogHeader>
            <DialogTitle>Carica foto 360°</DialogTitle>
            <DialogDescription>
              Le foto devono essere <strong>equirettangolari</strong> (rapporto 2:1, es. iPhone / Insta360 / Ricoh Theta).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Nome</Label>
              <Input value={upForm.nome} onChange={e => setUpForm(p => ({ ...p, nome: e.target.value }))} placeholder="es. Salone piano terra" data-testid="pano-up-name" />
            </div>
            <div>
              <Label>Note (opzionale)</Label>
              <Textarea value={upForm.note} onChange={e => setUpForm(p => ({ ...p, note: e.target.value }))} rows={2} data-testid="pano-up-note" />
            </div>
            <div className="border-2 border-dashed border-indigo-300 rounded-lg p-4 bg-indigo-50/30">
              <div className="flex gap-2 justify-center mb-3">
                <Button variant="outline" onClick={() => upFileRef.current?.click()} data-testid="pano-up-file">
                  <Upload className="h-4 w-4 mr-1" /> Sfoglia file
                </Button>
                <Button variant="outline" onClick={handleUpPaste} data-testid="pano-up-paste">
                  <Clipboard className="h-4 w-4 mr-1" /> Incolla
                </Button>
              </div>
              <input ref={upFileRef} type="file" accept="image/*" className="hidden" onChange={e => handleUpFile(e.target.files?.[0])} />
              {upForm.preview && (
                <div className="text-center">
                  <img src={upForm.preview} alt="preview" className="max-h-40 mx-auto rounded border" />
                  <div className="text-xs text-gray-600 mt-1">{upForm.file?.name}</div>
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUploadDlg(false)}>Annulla</Button>
            <Button onClick={submitUpload} disabled={saving} data-testid="pano-up-submit">
              {saving ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Salvataggio...</> : "Salva panorama"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Generate Dialog */}
      <Dialog open={generateDlg} onOpenChange={(o) => !generating && setGenerateDlg(o)}>
        <DialogContent className="max-w-2xl" data-testid="pano-gen-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Wand2 className="h-5 w-5 text-purple-600" /> Genera panorama 360° con AI</DialogTitle>
            <DialogDescription>
              L&apos;AI (Gemini Nano Banana) genererà un panorama equirettangolare a partire dal tuo prompt. Costo indicativo: 0.10 Crediti.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Nome</Label>
              <Input value={genForm.nome} onChange={e => setGenForm(p => ({ ...p, nome: e.target.value }))} placeholder="es. Salone moderno stile scandinavo" data-testid="pano-gen-name" />
            </div>
            <div>
              <Label>Descrizione (prompt) *</Label>
              <Textarea value={genForm.prompt} onChange={e => setGenForm(p => ({ ...p, prompt: e.target.value }))} rows={4}
                placeholder="es. Salone moderno con divano grigio, parete accento verde salvia, grandi vetrate sul giardino, luce naturale del mattino"
                data-testid="pano-gen-prompt" />
              <p className="text-xs text-gray-500 mt-1">L&apos;AI aggiungerà automaticamente i tag di proiezione equirettangolare 360°.</p>
            </div>
            <div>
              <Label>Immagine di riferimento (opzionale)</Label>
              <div className="flex items-center gap-2">
                <Button size="sm" variant="outline" onClick={() => genFileRef.current?.click()} data-testid="pano-gen-source-btn">
                  <Upload className="h-4 w-4 mr-1" /> {genForm.source_preview ? "Sostituisci" : "Carica riferimento"}
                </Button>
                {genForm.source_preview && (
                  <>
                    <img src={genForm.source_preview} alt="ref" className="h-12 w-24 object-cover rounded border" />
                    <Button size="sm" variant="ghost" onClick={() => setGenForm(p => ({ ...p, source_image: null, source_preview: null }))}>
                      <X className="h-4 w-4" />
                    </Button>
                  </>
                )}
              </div>
              <input ref={genFileRef} type="file" accept="image/*" className="hidden" onChange={e => handleGenSource(e.target.files?.[0])} />
            </div>
            {generating && (
              <AiProgressBar
                active={generating}
                phases={[
                  { until: 8, label: "Preparazione prompt equirettangolare..." },
                  { until: 25, label: "Invio a Gemini Nano Banana..." },
                  { until: 85, label: "Generazione panorama 360°..." },
                  { until: 100, label: "Salvataggio..." },
                ]}
                testid="pano-gen-progress"
              />
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setGenerateDlg(false)} disabled={generating}>Annulla</Button>
            <Button onClick={submitGenerate} disabled={generating} className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700" data-testid="pano-gen-submit">
              {generating ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Generazione...</> : <><Sparkles className="h-4 w-4 mr-1" /> Genera</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* QR Code Dialog */}
      <Dialog open={qrOpen} onOpenChange={setQrOpen}>
        <DialogContent className="max-w-md" data-testid="pano-qr-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><QrCode className="h-5 w-5 text-indigo-600" /> QR — {viewing?.nome}</DialogTitle>
            <DialogDescription>Scannerizza con lo smartphone per aprire il panorama e attivare la modalità VR / Cardboard.</DialogDescription>
          </DialogHeader>
          <div className="flex flex-col items-center gap-3 py-2">
            {viewing && (
              <div className="p-4 bg-white rounded-lg border shadow-sm">
                <QRCodeCanvas
                  value={`${window.location.origin}${window.location.pathname}?panorama=${viewing.id}`}
                  size={260}
                  includeMargin={true}
                  level="M"
                />
              </div>
            )}
            <div className="text-[11px] text-gray-500 text-center break-all px-2">
              {viewing && `${window.location.origin}${window.location.pathname}?panorama=${viewing.id}`}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setQrOpen(false)}>Chiudi</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Tour Editor Dialog - WIZARD 3 STEP */}
      <Dialog open={tourOpen} onOpenChange={(o) => !tourSaving && setTourOpen(o)}>
        <DialogContent className="max-w-5xl max-h-[90vh] flex flex-col" data-testid="pano-tour-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Route className="h-5 w-5 text-indigo-600" /> {tourId ? "Modifica Tour" : "Nuovo Tour Panorami"} — Wizard</DialogTitle>
            <DialogDescription>
              3 passi guidati: 1) seleziona e ordina · 2) anteprima 360° con salvataggio punti di vista · 3) nome e salvataggio.
              {tourId && <span className="ml-1 text-amber-800 font-medium">Stai modificando un tour esistente.</span>}
            </DialogDescription>
          </DialogHeader>

          {/* Step indicator */}
          <div className="flex items-center gap-2 border-b pb-3" data-testid="pano-tour-steps">
            {[
              { n: 1, label: "Seleziona & Ordina" },
              { n: 2, label: "Anteprima 360°" },
              { n: 3, label: "Applica al tour" },
            ].map((s, i, arr) => (
              <div key={s.n} className="flex items-center gap-2 flex-1">
                <button
                  type="button"
                  onClick={() => tourOrder.length >= 2 && setTourStep(s.n)}
                  className={`flex items-center gap-2 px-3 py-2 rounded-full text-sm font-medium transition ${
                    tourStep === s.n
                      ? "bg-indigo-600 text-white shadow"
                      : tourStep > s.n
                      ? "bg-emerald-100 text-emerald-800 hover:bg-emerald-200"
                      : "bg-gray-100 text-gray-500 hover:bg-gray-200"
                  }`}
                  data-testid={`pano-tour-step-${s.n}`}
                >
                  <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                    tourStep === s.n ? "bg-white text-indigo-600" : tourStep > s.n ? "bg-emerald-600 text-white" : "bg-white text-gray-600"
                  }`}>
                    {tourStep > s.n ? <Check className="h-3 w-3" /> : s.n}
                  </span>
                  <span className="hidden sm:inline">{s.label}</span>
                </button>
                {i < arr.length - 1 && <div className={`flex-1 h-0.5 ${tourStep > s.n ? "bg-emerald-400" : "bg-gray-200"}`} />}
              </div>
            ))}
          </div>

          <div className="overflow-y-auto flex-1 py-3">
            {/* ==================== STEP 1 ==================== */}
            {tourStep === 1 && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div>
                  <h4 className="text-sm font-medium mb-2 text-gray-700 flex items-center justify-between">
                    <span>Sequenza tour ({tourOrder.length})</span>
                    <span className="text-xs text-gray-500 font-normal italic">Trascina per riordinare · ✕ per rimuovere</span>
                  </h4>
                  <div className="space-y-2" data-testid="pano-tour-list">
                    {tourOrder.map((pid, idx) => {
                      const p = panorami.find(x => x.id === pid);
                      if (!p) return null;
                      return (
                        <div key={pid}
                          draggable
                          onDragStart={() => { if (renamingIdx !== idx) setTourDragIdx(idx); }}
                          onDragOver={(e) => e.preventDefault()}
                          onDrop={() => { if (tourDragIdx !== null) { moveTourItem(tourDragIdx, idx); setTourDragIdx(null); } }}
                          draggable={renamingIdx !== idx}
                          className={`flex items-center gap-2 p-2 border rounded bg-white hover:bg-indigo-50 group ${tourDragIdx === idx ? "opacity-50" : ""} ${renamingIdx === idx ? "cursor-text ring-2 ring-indigo-300" : "cursor-move"}`}
                          data-testid={`pano-tour-item-${idx}`}
                        >
                          <GripVertical className="h-4 w-4 text-gray-400 flex-shrink-0" />
                          <div className="w-7 h-7 rounded-full bg-indigo-600 text-white text-xs font-bold flex items-center justify-center flex-shrink-0">{idx + 1}</div>
                          <img src={p.thumbnail || p.image_base64} alt="" className="w-14 h-8 object-cover rounded flex-shrink-0" />
                          {renamingIdx === idx ? (
                            <input
                              type="text"
                              value={renameValue}
                              onChange={e => setRenameValue(e.target.value)}
                              onBlur={() => savePanoramaName(p.id, renameValue)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") { e.preventDefault(); savePanoramaName(p.id, renameValue); }
                                if (e.key === "Escape") { setRenamingIdx(-1); setRenameValue(""); }
                              }}
                              onClick={(e) => e.stopPropagation()}
                              className="text-sm flex-1 min-w-0 border-2 border-indigo-500 rounded px-2 py-0.5 focus:outline-none"
                              placeholder="es. Ingresso, Sala principale, Altare..."
                              maxLength={120}
                              autoFocus
                              data-testid={`pano-tour-rename-input-${idx}`}
                            />
                          ) : (
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); setRenamingIdx(idx); setRenameValue(p.nome || ""); }}
                              className="text-sm truncate flex-1 text-left hover:text-indigo-700 hover:underline decoration-dotted underline-offset-2"
                              title={`Clicca per rinominare (attuale: "${p.nome}")`}
                              data-testid={`pano-tour-rename-btn-${idx}`}
                            >
                              {p.nome || `Scena ${idx + 1}`}
                              <Wand2 className="h-3 w-3 inline-block ml-1 text-gray-400 group-hover:text-indigo-500" />
                            </button>
                          )}
                          {idx < tourOrder.length - 1 && <span className="text-xs text-emerald-600 flex-shrink-0">→</span>}
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); removeTourItem(idx); }}
                            className="ml-1 p-1 rounded-full text-red-500 hover:bg-red-100 opacity-60 group-hover:opacity-100 transition"
                            title="Rimuovi dalla sequenza (non elimina dalla libreria)"
                            data-testid={`pano-tour-remove-${idx}`}
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      );
                    })}
                    {tourOrder.length < 2 && (
                      <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-3">
                        Servono almeno 2 panorami per creare un tour. Puoi ripristinare tutti i panorami dal pulsante &quot;Ripristina tutti&quot;.
                      </div>
                    )}
                  </div>
                  {tourOrder.length < panorami.length && (
                    <Button size="sm" variant="outline" className="mt-2" onClick={() => setTourOrder(panorami.map(p => p.id))} data-testid="pano-tour-restore">
                      <Plus className="h-4 w-4 mr-1" /> Ripristina tutti ({panorami.length})
                    </Button>
                  )}
                </div>
                <div>
                  <h4 className="text-sm font-medium mb-2 text-gray-700">Preview mappa</h4>
                  <div className="bg-slate-50 border rounded p-3 min-h-[280px]" data-testid="pano-tour-minimap">
                    <svg viewBox="0 0 500 300" className="w-full h-64">
                      <defs>
                        <marker id="arrow" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="5" markerHeight="5" orient="auto"><path d="M 0 0 L 10 5 L 0 10 z" fill="#4f46e5" /></marker>
                      </defs>
                      {tourOrder.map((pid, idx) => {
                        if (idx === tourOrder.length - 1) return null;
                        const angle = (idx / Math.max(tourOrder.length, 1)) * Math.PI * 2;
                        const nextAngle = ((idx + 1) / Math.max(tourOrder.length, 1)) * Math.PI * 2;
                        const x1 = 250 + Math.cos(angle) * 110;
                        const y1 = 150 + Math.sin(angle) * 90;
                        const x2 = 250 + Math.cos(nextAngle) * 110;
                        const y2 = 150 + Math.sin(nextAngle) * 90;
                        return <line key={`l-${idx}`} x1={x1} y1={y1} x2={x2} y2={y2} stroke="#4f46e5" strokeWidth="2" strokeDasharray="4,3" markerEnd="url(#arrow)" />;
                      })}
                      {tourOrder.map((pid, idx) => {
                        const p = panorami.find(x => x.id === pid);
                        if (!p) return null;
                        const angle = (idx / Math.max(tourOrder.length, 1)) * Math.PI * 2;
                        const cx = 250 + Math.cos(angle) * 110;
                        const cy = 150 + Math.sin(angle) * 90;
                        return (
                          <g key={pid}>
                            <circle cx={cx} cy={cy} r="24" fill="#4f46e5" stroke="#fff" strokeWidth="2" />
                            <text x={cx} y={cy + 4} textAnchor="middle" fill="#fff" fontSize="13" fontWeight="700">{idx + 1}</text>
                            <text x={cx} y={cy + 42} textAnchor="middle" fill="#333" fontSize="10">{(p.nome || "").substring(0, 14)}</text>
                          </g>
                        );
                      })}
                    </svg>
                    <p className="text-[11px] text-gray-500 mt-1 text-center">Le frecce mostrano l&apos;ordine di navigazione tra i panorami.</p>
                  </div>
                </div>
              </div>
            )}

            {/* ==================== STEP 2 ==================== */}
            {tourStep === 2 && (
              <div className="space-y-3">
                {tourOrder.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">Nessun panorama in sequenza.</div>
                ) : (() => {
                  const pid = tourOrder[tourPreviewIdx];
                  const pano = panorami.find(x => x.id === pid);
                  const nextPano = tourPreviewIdx < tourOrder.length - 1 ? panorami.find(x => x.id === tourOrder[tourPreviewIdx + 1]) : null;
                  const fullSrc = tourPreviewFull[pid] || "";
                  return (
                    <div>
                      <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
                        <div className="text-sm">
                          <span className="text-gray-500">Anteprima {tourPreviewIdx + 1}/{tourOrder.length}: </span>
                          <span className="font-semibold text-indigo-700">{pano?.nome}</span>
                          {nextPano && <span className="text-xs text-emerald-600 ml-2">→ {nextPano.nome}</span>}
                          {tourViewpoints[tourPreviewIdx] && (tourViewpoints[tourPreviewIdx].yaw !== 0 || tourViewpoints[tourPreviewIdx].pitch !== 0) && (
                            <Badge className="ml-2 bg-emerald-100 text-emerald-800 text-[10px]">✓ Vista salvata</Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-1 flex-wrap">
                          <Button size="sm" variant="outline" disabled={tourPreviewIdx === 0} onClick={() => setTourPreviewIdx(i => Math.max(0, i - 1))} data-testid="pano-tour-prev">
                            ← Precedente
                          </Button>
                          <Button size="sm" variant="outline" disabled={tourPreviewIdx >= tourOrder.length - 1} onClick={() => setTourPreviewIdx(i => Math.min(tourOrder.length - 1, i + 1))} data-testid="pano-tour-next">
                            Successivo →
                          </Button>
                          <Button size="sm" onClick={captureCurrentViewpoint} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="pano-tour-save-vp">
                            <MapPin className="h-4 w-4 mr-1" /> Salva punto di vista
                          </Button>
                        </div>
                      </div>
                      <div className="w-full rounded-lg overflow-hidden border bg-black" style={{ height: 480 }} data-testid="pano-tour-preview-viewer">
                        {tourPreviewLoading || !fullSrc ? (
                          <div className="w-full h-full flex items-center justify-center text-white gap-2">
                            <Loader2 className="h-6 w-6 animate-spin" /> Caricamento panorama...
                          </div>
                        ) : (
                          <ReactPhotoSphereViewer
                            key={pid}
                            ref={tourPreviewViewerRef}
                            src={fullSrc}
                            height="100%"
                            width="100%"
                            littlePlanetButton={false}
                            defaultZoomLvl={0}
                            minFov={30}
                            maxFov={110}
                            defaultYaw={tourViewpoints[tourPreviewIdx]?.yaw || 0}
                            defaultPitch={tourViewpoints[tourPreviewIdx]?.pitch || 0}
                            navbar={["zoom", "move", "fullscreen"]}
                          />
                        )}
                      </div>
                      <p className="text-[11px] text-gray-500 mt-1 italic">Ruota la vista fino alla direzione desiderata (dove &quot;guarderà&quot; l&apos;utente arrivando qui) e clicca &quot;Salva punto di vista&quot;.</p>
                      {/* Thumbnails strip */}
                      <div className="mt-3 flex gap-2 overflow-x-auto pb-2" data-testid="pano-tour-strip">
                        {tourOrder.map((tid, i) => {
                          const tp = panorami.find(x => x.id === tid);
                          if (!tp) return null;
                          return (
                            <button
                              key={tid}
                              type="button"
                              onClick={() => setTourPreviewIdx(i)}
                              className={`flex-shrink-0 relative border-2 rounded overflow-hidden transition ${i === tourPreviewIdx ? "border-indigo-600 shadow-md" : "border-gray-200 hover:border-indigo-300"}`}
                              data-testid={`pano-tour-thumb-${i}`}
                            >
                              <img src={tp.thumbnail || tp.image_base64} alt="" className="w-24 h-14 object-cover" />
                              <span className="absolute top-0 left-0 bg-indigo-600 text-white text-[10px] px-1 rounded-br">{i + 1}</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}

            {/* ==================== STEP 3 ==================== */}
            {tourStep === 3 && (
              <div className="space-y-4">
                <div>
                  <Label className="text-sm font-semibold text-indigo-900">Nome del tour *</Label>
                  <Input
                    value={tourName}
                    onChange={e => setTourName(e.target.value)}
                    placeholder="Es. Tour della villa - piano terra"
                    className="mt-1 border-indigo-300 focus-visible:ring-indigo-500"
                    data-testid="pano-tour-name"
                  />
                  <p className="text-[11px] text-gray-500 mt-1 italic">Il nome proposto è modificabile. Sarà visibile nella lista Tour della galleria.</p>
                </div>
                <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
                  <h3 className="font-semibold text-indigo-900 flex items-center gap-2 mb-2">
                    <Link2 className="h-4 w-4" /> Dove viene salvato il tour?
                  </h3>
                  <div className="text-sm text-indigo-900 space-y-1">
                    <p>
                      Il tour viene salvato <strong>in due punti</strong>:
                    </p>
                    <p className="text-xs text-indigo-800">
                      • <strong>Entità Tour</strong> (<code className="bg-white px-1 rounded">design_tours</code>): nome, ordine, punti di vista salvati — visibile e riapribile dalla galleria.<br />
                      • <strong>Hotspot su ogni panorama</strong> (<code className="bg-white px-1 rounded">tour-seq-N</code>): la freccia cliccabile nel viewer 360° che porta al successivo, con il tuo punto di vista di arrivo.<br />
                      • Puoi <strong>riaprire</strong> il tour dalla lista per rieditarne ordine, nome o punti di vista in qualunque momento.
                    </p>
                  </div>
                </div>
                <div className="border rounded-lg p-3">
                  <h4 className="font-medium text-gray-800 mb-2 text-sm">Riepilogo</h4>
                  <ul className="text-sm text-gray-700 space-y-1">
                    <li>• <strong>{tourOrder.length}</strong> panorami nella sequenza</li>
                    <li>• <strong>{Math.max(0, tourOrder.length - 1)}</strong> hotspot direzionali verranno creati</li>
                    <li>• <strong>{tourViewpoints.filter(v => v && (v.yaw !== 0 || v.pitch !== 0)).length}</strong> punti di vista personalizzati salvati ({tourOrder.length - tourViewpoints.filter(v => v && (v.yaw !== 0 || v.pitch !== 0)).length} usano default 0°)</li>
                    {tourId && <li className="text-amber-800 font-medium">⚠ Stai <strong>modificando</strong> un tour esistente — verrà aggiornato mantenendo lo stesso ID.</li>}
                  </ul>
                  <div className="flex flex-wrap gap-1 mt-2">
                    {tourOrder.map((pid, i) => {
                      const p = panorami.find(x => x.id === pid);
                      if (!p) return null;
                      const vpSaved = tourViewpoints[i] && (tourViewpoints[i].yaw !== 0 || tourViewpoints[i].pitch !== 0);
                      return (
                        <Badge key={pid} variant="secondary" className={`text-xs ${vpSaved ? "bg-emerald-100 text-emerald-800" : ""}`}>
                          {i + 1}. {(p.nome || "").substring(0, 22)}{vpSaved ? " ✓" : ""}
                        </Badge>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </div>

          <DialogFooter className="border-t pt-3 flex-shrink-0 flex-wrap gap-2">
            <Button variant="outline" onClick={clearTourLinks} disabled={tourSaving} className="mr-auto text-red-600 border-red-300 hover:bg-red-50" data-testid="pano-tour-clear">
              <Trash2 className="h-4 w-4 mr-1" /> Rimuovi tour esistente
            </Button>
            {tourStep > 1 && (
              <Button variant="outline" onClick={() => setTourStep(s => s - 1)} disabled={tourSaving} data-testid="pano-tour-back">
                ← Indietro
              </Button>
            )}
            <Button variant="outline" onClick={() => setTourOpen(false)} disabled={tourSaving}>Chiudi</Button>
            {tourStep < 3 && (
              <Button
                onClick={() => setTourStep(s => s + 1)}
                disabled={tourOrder.length < 2}
                className="bg-indigo-600 hover:bg-indigo-700"
                data-testid="pano-tour-next-step"
              >
                Avanti →
              </Button>
            )}
            {tourStep === 3 && (
              <Button onClick={applyTour} disabled={tourSaving || tourOrder.length < 2 || !tourName.trim()} className="bg-gradient-to-r from-indigo-600 to-purple-600" data-testid="pano-tour-apply">
                {tourSaving ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Salvataggio...</> : <><Link2 className="h-4 w-4 mr-1" /> {tourId ? "Aggiorna tour" : "Salva tour"}</>}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ==================== EXTRACT FRAME DIALOG ==================== */}
      <Dialog open={extractOpen} onOpenChange={(o) => !extractSaving && setExtractOpen(o)}>
        <DialogContent className="max-w-2xl" data-testid="pano-extract-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Camera className="h-5 w-5 text-emerald-600" /> Estrai foto dal panorama</DialogTitle>
            <DialogDescription>
              Scegli cosa vuoi salvare in Galleria AI: la vista rettangolare corrente (2D) oppure il panorama 360° completo (equirettangolare, riutilizzabile in Inventario e AI Design).
            </DialogDescription>
          </DialogHeader>
          {!extractPreview ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 py-4">
              <button
                type="button"
                onClick={extractCurrentFrame}
                className="p-4 border-2 border-emerald-300 rounded-lg text-left hover:bg-emerald-50 hover:border-emerald-500 transition"
                data-testid="pano-extract-mode-current"
              >
                <div className="flex items-center gap-2 font-semibold text-emerald-800 mb-1"><Camera className="h-5 w-5" /> Vista corrente (2D)</div>
                <p className="text-xs text-gray-600">Cattura esattamente quello che vedi in questo momento (proiezione rettificata). Perfetto per l&apos;Inventario.</p>
              </button>
              <button
                type="button"
                onClick={extractFullPanorama}
                className="p-4 border-2 border-indigo-300 rounded-lg text-left hover:bg-indigo-50 hover:border-indigo-500 transition"
                data-testid="pano-extract-mode-full"
              >
                <div className="flex items-center gap-2 font-semibold text-indigo-800 mb-1"><Globe className="h-5 w-5" /> Panorama 360° completo</div>
                <p className="text-xs text-gray-600">Salva l&apos;immagine equirettangolare intera (2:1). Utilizzabile come base per nuovi tour e refine batch.</p>
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="rounded-lg overflow-hidden border bg-slate-900 flex items-center justify-center" style={{ maxHeight: 340 }}>
                <img src={extractPreview.dataUrl} alt="preview" className="max-h-[340px] object-contain" data-testid="pano-extract-preview" />
              </div>
              <div className="flex items-center gap-2 text-xs">
                <Badge className={extractPreview.mode === "full360" ? "bg-indigo-100 text-indigo-800" : "bg-emerald-100 text-emerald-800"}>
                  {extractPreview.mode === "full360" ? "🌐 360° equirettangolare" : "📸 Vista 2D corrente"}
                </Badge>
              </div>
              <div>
                <Label className="text-sm font-semibold">Nome del file *</Label>
                <Input value={extractName} onChange={e => setExtractName(e.target.value)} placeholder="Es. Cucina - vista tavolo" data-testid="pano-extract-name" />
                <p className="text-[11px] text-gray-500 mt-1 italic">Sarà visibile nella Galleria AI del Design Assistant e selezionabile da Inventario.</p>
              </div>
            </div>
          )}
          <DialogFooter className="border-t pt-3 flex-wrap gap-2">
            {extractPreview && (
              <Button variant="outline" onClick={() => setExtractPreview(null)} disabled={extractSaving} className="mr-auto">
                ← Cambia modalità
              </Button>
            )}
            <Button variant="outline" onClick={() => { setExtractOpen(false); setExtractPreview(null); }} disabled={extractSaving}>Chiudi</Button>
            {extractPreview && (
              <Button onClick={saveExtractedFrame} disabled={extractSaving || !extractName.trim()} className="bg-emerald-600 hover:bg-emerald-700" data-testid="pano-extract-save">
                {extractSaving ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Salvataggio...</> : <><Check className="h-4 w-4 mr-1" /> Salva in Galleria</>}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Fase 2 (Feb 2026): Virtual Tour player Matterport-style */}
      {vtourOpen && vtourData && (
        <VirtualTourViewer
          tour={vtourData}
          panoramiFull={vtourPanos}
          onClose={() => { setVtourOpen(false); setVtourData(null); setVtourPanos([]); }}
        />
      )}

      {/* Fase 3 (Feb 2026): 3D Walkable Viewer with Depth AI (real parallax) */}
      {walkableOpen && viewing && (
        <WalkableViewer
          panorama={viewing}
          authToken={authToken}
          onClose={() => setWalkableOpen(false)}
        />
      )}

    </div>
  );
}
